/**
 * The ONE mapping from a saved survey to what the preview renders — used by
 * the detail header's Preview and the expanded row's Preview, so both show
 * the survey's public logo snapshot, its line above the title (custom or the
 * project default), its layout, its thank-you message and its website button
 * (link + text) exactly as the public page does.
 */
import { surveyWebsiteButton, type SurveyWebsiteButton, type SurveyWire } from '@releve/shared';
import type { SurveyPreviewContent } from './SurveyPreviewDialog';
import { surveyLogoUrl } from './surveyLogoUrl';

export function toSurveyPreviewContent(
  saved: Pick<
    SurveyWire,
    | 'title'
    | 'description'
    | 'questions'
    | 'thank_you_message'
    | 'public_logo_path'
    | 'website_url'
    | 'website_label'
    | 'project'
    | 'intro_line'
    | 'display_mode'
  >,
): SurveyPreviewContent {
  return {
    title: saved.title,
    description: saved.description,
    questions: saved.questions,
    thankYouMessage: saved.thank_you_message,
    logoUrl: surveyLogoUrl(saved.public_logo_path),
    websiteUrl: saved.website_url,
    websiteLabel: saved.website_label,
    introLine: saved.intro_line,
    projectName: saved.project?.name ?? null,
    displayMode: saved.display_mode,
  };
}

/**
 * The website button the public page will show — mirrors `get_public_survey`
 * + the API: the survey's own link when set (non-blank, `isCustom`), else the
 * org's website; the label through the shared `surveyWebsiteButton` (the same
 * rule the public page uses). NULL → no button.
 */
export function previewSurveyWebsiteButton(input: {
  override: string | null | undefined;
  label: string | null | undefined;
  orgWebsite: string | null | undefined;
  orgName: string;
}): SurveyWebsiteButton | null {
  const isCustom = Boolean(input.override?.trim());
  return surveyWebsiteButton({
    url: isCustom ? input.override : input.orgWebsite,
    label: input.label,
    isCustom,
    orgName: input.orgName,
  });
}
